<script lang="ts">
    import type { Snippet } from "svelte";
    import { fly } from "svelte/transition";
    import { LL } from "../../../i18n/i18n-svelte";
    import { isMac } from "../../WebRtc/DeviceUtils";
    import type { ContentAction } from "../../Utils/svelte-floatingui";

    interface Props {
        helpMedia?: string | null;
        hasImage?: boolean;
        hasDesc?: boolean;
        title?: string;
        desc?: string;
        delayBeforeAppear?: number;
        shortcuts?: string[];
        children?: Snippet;
        floatingAction: ContentAction;
    }

    let {
        helpMedia = null,
        hasImage = true,
        hasDesc = true,
        title = "Find people and navigate to them",
        desc = "Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.",
        delayBeforeAppear = 500,
        shortcuts = [],
        children,
        floatingAction,
    }: Props = $props();

    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
</script>

<div
    data-testid="action-help-tooltip"
    use:floatingAction
    class="p-1.5 {hasImage && hasDesc
        ? 'w-64'
        : 'min-w-[128px] text-center'} z-[500] max-w-[calc(100dvw-16px)] rounded-[var(--nutrivera-shell-radius)] border border-solid border-[var(--nutrivera-shell-border)] bg-[var(--nutrivera-shell-bg)] text-[var(--nutrivera-shell-text)] shadow-[0_8px_24px_rgb(0_0_0/38%)]"
    in:fly={{ delay: reducedMotion ? 0 : delayBeforeAppear, y: reducedMotion ? 0 : 12, duration: reducedMotion ? 0 : 200 }}
>
    <div class="relative z-10 pb-3 overflow-hidden flex flex-col gap-2">
        {#if helpMedia}
            {#if helpMedia.endsWith(".mp4")}
                <video autoplay muted loop class="w-full rounded-md">
                    <source src={helpMedia} type="video/mp4" />
                    {$LL.actionbar.help.videoNotSupported()}
                </video>
            {:else}
                <img alt="Help GIF" src={helpMedia} class="w-full rounded-md" loading="lazy" draggable="false" />
            {/if}
        {/if}
        <div class="flex flex-col gap-2 items-start px-1 pt-1">
            <div class="flex flex-row items-start justify-between w-full">
                <div class="text-lg bold leading-none">
                    {title}
                </div>
                {#if shortcuts.length > 0}
                    <div class="ps-4 text-xs opacity-80 pb-1 flex flex-row items-center gap-1">
                        {#each shortcuts as shortcut, index (index)}
                            <div
                                class="text-sm h-[26px] leading-none font-bold p-1.5 rounded border border-solid border-white/50 flex items-center justify-center w-fit shadow-white/50 shadow-[0_2px_0px_rgba(0,0,0,0.50)] mb-[2px] {shortcut.length <
                                2
                                    ? ' aspect-square'
                                    : ''}"
                            >
                                {isMac() ? shortcut.replace(/\bctrl\b/gi, "cmd") : shortcut}
                            </div>
                            {#if index < shortcuts.length - 1}
                                <span class="text-sm font-bold"> + </span>
                            {/if}
                        {/each}
                    </div>
                {/if}
            </div>
            {#if hasDesc}
                <div class="text-xs italic opacity-80">
                    {desc}
                </div>
            {/if}
        </div>
    </div>
    {@render children?.()}
</div>
